// Phân tích chuyên sâu cho trang Tổng quan: tổng quan, mức độ đọc, quy trình duyệt, hiệu lực & rà soát, đa chiều, lượt truy cập.
// Hàm thuần — nhận dữ liệu + kỳ báo cáo, trả về số liệu để vẽ và xuất Excel.
import dayjs, { type Dayjs } from 'dayjs';
import { DEPARTMENTS, blockName, deptName, type Doc, type DocCategory, type DocType, type Employee } from './data';
import { docState, recipientIds } from './logic';
import {
  EXPIRING_DAYS, SLA_DAYS, avg, docFlags, inRange, isActiveAt, readRateAt, type Flag, type Period,
} from './stats';
import { EXT_GROUPS } from './utils';

const DAY = 86400000;
const daysBetween = (from: string, to: string) => (new Date(to).getTime() - new Date(from).getTime()) / DAY;
const isIssued = (d: Doc) => d.status === 'published' || d.status === 'revoked';

export const median = (xs: number[]) => {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
export const percentile = (xs: number[], q: number) => {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.ceil(q * s.length) - 1)];
};

// ---------------------------------------------------------------------------
// Tổng quan

export function overviewAnalytics(docs: Doc[], scoped: Doc[], allDocs: Doc[], people: Employee[], p: Period, now: Dayjs) {
  const byId = new Map(allDocs.map(d => [d.id, d]));
  const flags = new Map<string, Flag[]>(docs.map(d => [d.id, docFlags(d, people, now)]));
  const flagged = (f: Flag) => docs.filter(d => flags.get(d.id)!.includes(f));

  const monthly = Array.from({ length: 12 }, (_, i) => {
    const m = now.subtract(11 - i, 'month');
    const s = m.startOf('month');
    const e = m.endOf('month');
    return {
      label: `T${m.month() + 1}`,
      full: m.format('MM/YYYY'),
      issued: docs.filter(d => inRange(d.approvedAt, s, e)).length,
      revoked: docs.filter(d => inRange(d.revokedAt, s, e)).length,
      lastYear: docs.filter(d => inRange(d.approvedAt, s.subtract(1, 'year'), e.subtract(1, 'year'))).length,
    };
  });

  const stateCount: Record<string, number> = { active: 0, upcoming: 0, expired: 0, revoked: 0, working: 0 };
  docs.forEach(d => {
    const st = docState(d);
    stateCount[st === 'replaced' ? 'expired' : st === 'draft' || st === 'pending' || st === 'rejected' ? 'working' : st]++;
  });

  const deptRead = new Map<string, { total: number; read: number; unread: Map<string, Doc[]> }>();
  docs.filter(isIssued).forEach(d => {
    recipientIds(d, people).forEach(pid => {
      const person = people.find(x => x.id === pid);
      if (!person) return;
      const row = deptRead.get(person.deptId) || { total: 0, read: 0, unread: new Map<string, Doc[]>() };
      row.total++;
      if (d.views[pid]) row.read++;
      else row.unread.set(pid, [...(row.unread.get(pid) || []), d]);
      deptRead.set(person.deptId, row);
    });
  });
  const depts = DEPARTMENTS.map(dp => ({ id: dp.id, name: dp.name, ...(deptRead.get(dp.id) || { total: 0, read: 0, unread: new Map<string, Doc[]>() }) }))
    .filter(x => x.total > 0)
    .map(x => ({ ...x, rate: x.read / x.total }))
    .sort((a, b) => b.rate - a.rate);

  const events = approvalEvents(docs).filter(e => e.outcome === 'approved' && e.days !== null);
  const slaIn = (from: Dayjs, to: Dayjs) => events.filter(e => inRange(e.at, from, to)).map(e => e.days as number);
  const slaCur = slaIn(p.start, p.end);
  const slaPrev = slaIn(p.prevStart, p.prevEnd);
  const slaTrend = Array.from({ length: 6 }, (_, i) => {
    const m = now.subtract(5 - i, 'month');
    const xs = slaIn(m.startOf('month'), m.endOf('month'));
    return { label: `T${m.month() + 1}`, days: xs.length ? Math.round(avg(xs) * 10) / 10 : null };
  });

  const files = scoped.flatMap(d => d.files);
  const storage = EXT_GROUPS.map(g => ({ key: g.key, label: g.label, size: files.filter(f => g.exts.includes(f.ext)).reduce((s, f) => s + f.size, 0) }));

  return {
    active: docs.filter(d => docState(d) === 'active').length,
    activePrev: docs.filter(d => isActiveAt(d, p.start, byId)).length,
    issued: docs.filter(d => inRange(d.approvedAt, p.start, p.end)).length,
    issuedPrev: docs.filter(d => inRange(d.approvedAt, p.prevStart, p.prevEnd)).length,
    pending: docs.filter(d => d.status === 'pending').length,
    readNow: readRateAt(docs, people, now),
    readPrev: readRateAt(docs, people, p.start),
    monthly, stateCount, depts,
    overdue: flagged('overdue'), expiring: flagged('expiring'), lowread: flagged('lowread'), comments: flagged('comments'),
    rejected: docs.filter(d => d.status === 'rejected'),
    slaAvg: avg(slaCur), slaPrevAvg: avg(slaPrev), slaCount: slaCur.length,
    slaWithin: slaCur.length ? slaCur.filter(x => x <= SLA_DAYS).length / slaCur.length : 0,
    slaTrend, storage, used: files.reduce((s, f) => s + f.size, 0),
    activity: docs.flatMap(d => d.history.filter(h => h.by !== 'Hệ thống').map(h => ({ ...h, doc: d })))
      .sort((a, b) => b.at.localeCompare(a.at)).slice(0, 8),
  };
}

// ---------------------------------------------------------------------------
// Mức độ đọc — nhóm tài liệu ban hành trong kỳ (cohort), tính theo cặp (tài liệu × người được chia sẻ)

export const READ_BUCKETS = ['≤ 1 ngày', '1–3 ngày', '3–7 ngày', '> 7 ngày', 'Chưa đọc'] as const;
const readBucket = (delay: number | null) => (delay === null ? 4 : delay <= 1 ? 0 : delay <= 3 ? 1 : delay <= 7 ? 2 : 3);

export interface ReadPair { doc: Doc; person: Employee; viewedAt: string | null; delay: number | null }

export function readPairs(cohort: Doc[], people: Employee[]): ReadPair[] {
  return cohort.flatMap(d => recipientIds(d, people).map(pid => {
    const person = people.find(x => x.id === pid)!;
    const viewedAt = d.views[pid] || null;
    return { doc: d, person, viewedAt, delay: viewedAt && d.approvedAt ? Math.max(0, daysBetween(d.approvedAt, viewedAt)) : null };
  })).filter(x => x.person);
}

export function readingAnalytics(docs: Doc[], people: Employee[], types: DocType[], p: Period, now: Dayjs) {
  const cohort = docs.filter(d => isIssued(d) && inRange(d.approvedAt, p.start, p.end));
  const pairs = readPairs(cohort, people);
  const delays = pairs.filter(x => x.delay !== null).map(x => x.delay as number);
  const buckets = READ_BUCKETS.map((label, i) => ({ label, count: pairs.filter(x => readBucket(x.delay) === i).length }));
  const curve = Array.from({ length: 31 }, (_, day) => ({
    day,
    pct: pairs.length ? Math.round((pairs.filter(x => x.delay !== null && x.delay <= day).length / pairs.length) * 1000) / 10 : 0,
  }));

  // Ma trận phòng ban × loại tài liệu
  const usedTypes = types.filter(t => cohort.some(d => d.typeId === t.id));
  const matrix = DEPARTMENTS.map(dp => ({
    dept: dp,
    cells: usedTypes.map(t => {
      const xs = pairs.filter(x => x.person.deptId === dp.id && x.doc.typeId === t.id);
      return { typeId: t.id, total: xs.length, read: xs.filter(x => x.viewedAt).length };
    }),
  })).filter(r => r.cells.some(c => c.total));

  const lowDocs = cohort.map(d => {
    const xs = pairs.filter(x => x.doc.id === d.id);
    const read = xs.filter(x => x.viewedAt).length;
    return { doc: d, total: xs.length, read, unread: xs.length - read, rate: xs.length ? read / xs.length : 1, ageDays: d.approvedAt ? Math.floor(daysBetween(d.approvedAt, now.toISOString())) : 0 };
  }).filter(x => x.total >= 3 && x.unread > 0).sort((a, b) => a.rate - b.rate || b.unread - a.unread).slice(0, 10);

  const byPerson = new Map<string, { person: Employee; unread: Doc[]; total: number }>();
  pairs.forEach(x => {
    const row = byPerson.get(x.person.id) || { person: x.person, unread: [], total: 0 };
    row.total++;
    if (!x.viewedAt) row.unread.push(x.doc);
    byPerson.set(x.person.id, row);
  });
  const topPeople = Array.from(byPerson.values()).filter(x => x.unread.length)
    .map(x => ({ ...x, oldestDays: Math.max(...x.unread.map(d => (d.approvedAt ? Math.floor(daysBetween(d.approvedAt, now.toISOString())) : 0))) }))
    .sort((a, b) => b.unread.length - a.unread.length || b.oldestDays - a.oldestDays).slice(0, 10);

  const read = pairs.filter(x => x.viewedAt).length;
  return {
    cohort: cohort.length, pairs: pairs.length, read, unread: pairs.length - read,
    rate: pairs.length ? read / pairs.length : 0,
    medianDays: median(delays),
    within24: pairs.length ? delays.filter(x => x <= 1).length / pairs.length : 0,
    downloads: docs.reduce((s, d) => s + d.access.filter(a => a.action === 'download' && inRange(a.at, p.start, p.end)).length, 0),
    buckets, curve, usedTypes, matrix, lowDocs, topPeople,
  };
}

// ---------------------------------------------------------------------------
// Quy trình duyệt — dựng sự kiện duyệt/từ chối từ lịch sử

export interface ApprovalEvent { doc: Doc; approver: string; outcome: 'approved' | 'rejected'; submittedAt?: string; at: string; days: number | null; note?: string }

export function approvalEvents(docs: Doc[]): ApprovalEvent[] {
  const out: ApprovalEvent[] = [];
  docs.forEach(d => {
    let lastSubmit: string | undefined;
    [...d.history].sort((a, b) => a.at.localeCompare(b.at)).forEach(h => {
      if (h.action === 'Gửi duyệt') lastSubmit = h.at;
      else if (h.action === 'Duyệt & ban hành' || h.action === 'Từ chối') {
        out.push({
          doc: d, approver: h.by, outcome: h.action === 'Từ chối' ? 'rejected' : 'approved', submittedAt: lastSubmit, at: h.at,
          days: lastSubmit ? Math.max(0, daysBetween(lastSubmit, h.at)) : null, note: h.note,
        });
        lastSubmit = undefined;
      }
    });
  });
  return out;
}

export const DURATION_BUCKETS = [
  { label: '< 4 giờ', max: 4 / 24 }, { label: '4–24 giờ', max: 1 }, { label: '1–2 ngày', max: 2 },
  { label: '2–3 ngày', max: 3 }, { label: '3–5 ngày', max: 5 }, { label: '> 5 ngày', max: Infinity },
];
export const AGING_BUCKETS = [{ label: '< 1 ngày', max: 1 }, { label: '1–3 ngày', max: 3 }, { label: '3–7 ngày', max: 7 }, { label: '> 7 ngày', max: Infinity }];
const bucketOf = <T extends { max: number }>(buckets: T[], v: number) => buckets.findIndex(b => v <= b.max);

export function approvalAnalytics(docs: Doc[], p: Period, now: Dayjs) {
  const all = approvalEvents(docs);
  const events = all.filter(e => inRange(e.at, p.start, p.end));
  const approved = events.filter(e => e.outcome === 'approved');
  const durations = approved.filter(e => e.days !== null).map(e => e.days as number);
  const rejected = events.filter(e => e.outcome === 'rejected');
  const firstPass = approved.filter(e => !all.some(x => x.doc.id === e.doc.id && x.outcome === 'rejected' && x.at < e.at));

  // Phễu: tài liệu tạo trong kỳ
  const created = docs.filter(d => inRange(d.createdAt, p.start, p.end));
  const hasAction = (d: Doc, a: string) => d.history.some(h => h.action === a);
  const funnel = [
    { key: 'created', label: 'Tạo mới', count: created.length },
    { key: 'submitted', label: 'Đã gửi duyệt', count: created.filter(d => hasAction(d, 'Gửi duyệt') || d.approvedAt).length },
    { key: 'approved', label: 'Đã ban hành', count: created.filter(d => !!d.approvedAt).length },
  ];
  const funnelSide = {
    rejectedOnce: created.filter(d => hasAction(d, 'Từ chối')).length,
    pending: created.filter(d => d.status === 'pending').length,
    draft: created.filter(d => d.status === 'draft').length,
    rejected: created.filter(d => d.status === 'rejected').length,
  };

  // Thời gian từng bước
  const firstSubmit = (d: Doc) => [...d.history].sort((a, b) => a.at.localeCompare(b.at)).find(h => h.action === 'Gửi duyệt')?.at;
  const draftDays = docs.map(d => ({ d, s: firstSubmit(d) })).filter(x => x.s && inRange(x.s, p.start, p.end)).map(x => daysBetween(x.d.createdAt, x.s!));
  const reworkDays = all.filter(e => e.outcome === 'rejected' && inRange(e.at, p.start, p.end)).map(e => {
    const next = all.find(x => x.doc.id === e.doc.id && x.submittedAt && x.submittedAt > e.at);
    return next?.submittedAt ? daysBetween(e.at, next.submittedAt) : null;
  }).filter((x): x is number => x !== null);
  const cycleDays = docs.filter(d => inRange(d.approvedAt, p.start, p.end) && d.history.some(h => h.action === 'Gửi duyệt')).map(d => daysBetween(d.createdAt, d.approvedAt!));
  const steps = [
    { key: 'draft', label: 'Soạn thảo → gửi duyệt', days: avg(draftDays), n: draftDays.length },
    { key: 'wait', label: 'Chờ duyệt', days: avg(durations), n: durations.length },
    { key: 'rework', label: 'Sửa sau từ chối', days: avg(reworkDays), n: reworkDays.length },
  ];

  const histogram = DURATION_BUCKETS.map((b, i) => ({ label: b.label, count: durations.filter(x => bucketOf(DURATION_BUCKETS, x) === i).length, overSla: (DURATION_BUCKETS[i - 1]?.max ?? 0) >= SLA_DAYS }));

  const pendingDocs = docs.filter(d => d.status === 'pending').map(d => ({ doc: d, age: d.submittedAt ? daysBetween(d.submittedAt, now.toISOString()) : 0 }))
    .sort((a, b) => b.age - a.age);
  const aging = AGING_BUCKETS.map((b, i) => ({ label: b.label, count: pendingDocs.filter(x => bucketOf(AGING_BUCKETS, x.age) === i).length }));

  const byApprover = new Map<string, { name: string; approved: number; rejected: number; durations: number[] }>();
  events.forEach(e => {
    const row = byApprover.get(e.approver) || { name: e.approver, approved: 0, rejected: 0, durations: [] };
    if (e.outcome === 'approved') row.approved++; else row.rejected++;
    if (e.days !== null) row.durations.push(e.days);
    byApprover.set(e.approver, row);
  });
  const approvers = Array.from(byApprover.values()).map(r => ({
    ...r, total: r.approved + r.rejected, avgDays: avg(r.durations), sla: r.durations.length ? r.durations.filter(x => x <= SLA_DAYS).length / r.durations.length : 0,
  })).sort((a, b) => b.total - a.total);

  return {
    decisions: events.length, approved: approved.length, rejected: rejected.length,
    avgDays: avg(durations), medianDays: median(durations), p90Days: percentile(durations, 0.9),
    sla: durations.length ? durations.filter(x => x <= SLA_DAYS).length / durations.length : 0,
    rejectRate: events.length ? rejected.length / events.length : 0,
    firstPass: approved.length ? firstPass.length / approved.length : 0,
    cycleDays: avg(cycleDays), cycleCount: cycleDays.length,
    funnel, funnelSide, steps, histogram, aging, pendingDocs, approvers,
    rejections: rejected.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 6),
  };
}

// ---------------------------------------------------------------------------
// Hiệu lực & rà soát

export const REVIEW_MONTHS = 12;
export const HORIZON_DAYS = 90;
export const needsReview = (d: Doc, now: Dayjs) =>
  docState(d, now.format('YYYY-MM-DD')) === 'active' && !!d.approvedAt && dayjs(d.reviewedAt || d.approvedAt).isBefore(now.subtract(REVIEW_MONTHS, 'month'));

export const AGE_BUCKETS = [{ label: '< 6 tháng', max: 6 }, { label: '6–12 tháng', max: 12 }, { label: '1–2 năm', max: 24 }, { label: '> 2 năm', max: Infinity }];

export function lifecycleAnalytics(docs: Doc[], categories: DocCategory[], p: Period, now: Dayjs) {
  const today = now.format('YYYY-MM-DD');
  const horizon = now.add(HORIZON_DAYS, 'day').format('YYYY-MM-DD');
  const active = docs.filter(d => docState(d, today) === 'active');
  const ages = active.map(d => now.diff(dayjs(d.effectiveFrom), 'month', true));

  const expiringSoon = active.filter(d => d.effectiveTo && d.effectiveTo <= horizon);
  const startingSoon = docs.filter(d => docState(d, today) === 'upcoming' && d.effectiveFrom <= horizon);
  const weeks = Array.from({ length: Math.ceil(HORIZON_DAYS / 7) }, (_, i) => {
    const s = now.add(i * 7, 'day').format('YYYY-MM-DD');
    const e = now.add(i * 7 + 6, 'day').format('YYYY-MM-DD');
    return {
      label: dayjs(s).format('DD/MM'),
      expiring: expiringSoon.filter(d => d.effectiveTo! >= s && d.effectiveTo! <= e).length,
      starting: startingSoon.filter(d => d.effectiveFrom >= s && d.effectiveFrom <= e).length,
    };
  });
  const timeline = [
    ...expiringSoon.map(d => ({ doc: d, kind: 'expire' as const, date: d.effectiveTo! })),
    ...startingSoon.map(d => ({ doc: d, kind: 'start' as const, date: d.effectiveFrom })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  const review = active.filter(d => needsReview(d, now))
    .map(d => ({ doc: d, months: Math.floor(now.diff(dayjs(d.reviewedAt || d.approvedAt), 'month', true)) }))
    .sort((a, b) => b.months - a.months);

  const leaves = categories.filter(c => c.active && !categories.some(x => x.parentId === c.id));
  const emptyCats = leaves.filter(c => !active.some(d => d.categoryId === c.id))
    .map(c => ({ cat: c, total: docs.filter(d => d.categoryId === c.id).length }));

  const reissues = docs.filter(d => d.replacesId && inRange(d.approvedAt, p.start, p.end))
    .map(d => ({ doc: d, old: docs.find(x => x.id === d.replacesId) }))
    .sort((a, b) => (b.doc.approvedAt || '').localeCompare(a.doc.approvedAt || ''));

  return {
    active: active.length,
    avgAgeMonths: avg(ages),
    ageBuckets: AGE_BUCKETS.map((b, i) => ({ label: b.label, count: ages.filter(a => bucketOf(AGE_BUCKETS, a) === i).length })),
    noExpiry: active.filter(d => !d.effectiveTo).length,
    expiringSoon: expiringSoon.length, expiring30: active.filter(d => d.effectiveTo && d.effectiveTo <= now.add(EXPIRING_DAYS, 'day').format('YYYY-MM-DD')).length,
    startingSoon: startingSoon.length, weeks, timeline,
    review, emptyCats, reissues, reissueTotal: docs.filter(d => d.replacesId).length,
  };
}

// ---------------------------------------------------------------------------
// Phân tích đa chiều

export type Dimension = 'type' | 'category' | 'unit' | 'block' | 'creator';
export const DIMENSION_LABEL: Record<Dimension, string> = { type: 'Loại tài liệu', category: 'Danh mục', unit: 'Đơn vị ban hành', block: 'Khối/nghiệp vụ', creator: 'Người tạo' };

export interface DimRow {
  key: string; label: string; filter?: Record<string, string>;
  total: number; active: number; issued: number; pending: number;
  readRate: number; readPairs: number; avgApproval: number; rejectRate: number; decisions: number; expiring: number; size: number;
}

export function dimensionAnalytics(docs: Doc[], people: Employee[], categories: DocCategory[], types: DocType[], dim: Dimension, p: Period, now: Dayjs): DimRow[] {
  const catById = new Map(categories.map(c => [c.id, c]));
  const rootOf = (catId: string) => {
    let cur = catById.get(catId);
    while (cur?.parentId) cur = catById.get(cur.parentId);
    return cur;
  };
  const keyOf = (d: Doc) => (dim === 'type' ? d.typeId : dim === 'category' ? rootOf(d.categoryId)?.id || '—' : dim === 'unit' ? d.unitId : dim === 'block' ? d.blockId : d.createdBy);
  const labelOf = (k: string, d: Doc) => (dim === 'type' ? types.find(t => t.id === k)?.name || '—'
    : dim === 'category' ? rootOf(d.categoryId)?.name || '—'
    : dim === 'unit' ? deptName(k) : dim === 'block' ? blockName(k) : d.createdByName);
  const filterOf = (k: string): Record<string, string> | undefined => (dim === 'type' ? { type: k } : dim === 'category' ? { cat: k } : dim === 'unit' ? { unit: k } : dim === 'block' ? { block: k } : undefined);
  const events = approvalEvents(docs).filter(e => inRange(e.at, p.start, p.end));
  const expiringBy = now.add(EXPIRING_DAYS, 'day').format('YYYY-MM-DD');

  const groups = new Map<string, Doc[]>();
  docs.forEach(d => groups.set(keyOf(d), [...(groups.get(keyOf(d)) || []), d]));
  return Array.from(groups.entries()).map(([k, list]) => {
    const issued = list.filter(isIssued);
    let pairs = 0;
    let read = 0;
    issued.forEach(d => { const ids = recipientIds(d, people); pairs += ids.length; read += ids.filter(id => d.views[id]).length; });
    const ev = events.filter(e => keyOf(e.doc) === k);
    const durations = ev.filter(e => e.outcome === 'approved' && e.days !== null).map(e => e.days as number);
    return {
      key: k, label: labelOf(k, list[0]), filter: filterOf(k),
      total: list.length,
      active: list.filter(d => docState(d) === 'active').length,
      issued: list.filter(d => inRange(d.approvedAt, p.start, p.end)).length,
      pending: list.filter(d => d.status === 'pending').length,
      readRate: pairs ? read / pairs : 0, readPairs: pairs,
      avgApproval: avg(durations),
      rejectRate: ev.length ? ev.filter(e => e.outcome === 'rejected').length / ev.length : 0, decisions: ev.length,
      expiring: list.filter(d => docState(d) === 'active' && d.effectiveTo && d.effectiveTo <= expiringBy).length,
      size: list.reduce((s, d) => s + d.files.reduce((a, f) => a + f.size, 0), 0),
    };
  }).sort((a, b) => b.total - a.total);
}

// ---------------------------------------------------------------------------
// Lượt xem / tải theo thời gian

export function accessSeries(docs: Doc[], p: Period) {
  const span = p.end.diff(p.start, 'day');
  const unit: 'day' | 'week' | 'month' = span <= 45 ? 'day' : span <= 200 ? 'week' : 'month';
  const buckets: { label: string; start: Dayjs; end: Dayjs; views: number; downloads: number }[] = [];
  for (let s = p.start.startOf(unit); s.isBefore(p.end); s = s.add(1, unit)) {
    buckets.push({ label: unit === 'month' ? `T${s.month() + 1}` : s.format('DD/MM'), start: s, end: s.add(1, unit), views: 0, downloads: 0 });
  }
  const per = new Map<string, { doc: Doc; views: number; downloads: number }>();
  docs.forEach(d => d.access.forEach(a => {
    if (!inRange(a.at, p.start, p.end) || (a.action !== 'view' && a.action !== 'download')) return;
    const b = buckets.find(x => a.at >= x.start.toISOString() && a.at < x.end.toISOString());
    if (b) b[a.action === 'view' ? 'views' : 'downloads']++;
    const row = per.get(d.id) || { doc: d, views: 0, downloads: 0 };
    row[a.action === 'view' ? 'views' : 'downloads']++;
    per.set(d.id, row);
  }));
  return {
    unit,
    series: buckets.map(({ label, views, downloads }) => ({ label, views, downloads })),
    top: Array.from(per.values()).sort((a, b) => b.views - a.views).slice(0, 5),
  };
}
